import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import type { IdGenerator } from "../../../rxjs/configureEpics"
import type {
    UserAccount,
    UserAccountAddress,
    UserAccountLocation,
    UserAccountNote,
    UserAccountRole,
} from "../../../data/types"
import type { StateObject } from "../../../redux/types"
import { getSelectedAccountId } from "../../../auth/selectors"
import { getUserAccountById } from "../../../data/selectors"
import { composeFullName } from "../../../../utils/general"
import { EmployeeFormValues, EmployeeNote, SelectedPosition } from "./form/types"
import { getUtcTimestamp } from "../../../../utils/dateFunctions"
import {
    EmployeeAddressEventData,
    EmployeeCreateRequestedEventData,
    EmployeeLocationEventData,
    EmployeeNoteEventData,
    EmployeePositionEventData,
    EmployeeUpdateRequestedEventData,
} from "./types"
import * as uuid from "uuid"
import { getIsoDateTimeNow } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/current"

export const composeUserAccount = (
    newValue: EmployeeFormValues,
    oldValue: Maybe<EmployeeFormValues>,
    userAccountId: string,
    idgen: IdGenerator,
    state: StateObject
): Partial<UserAccount> => {
    const accountId = getSelectedAccountId(state) || ""
    const oldUserAccountData: any = getUserAccountById(state, userAccountId) || {}
    return {
        id: userAccountId,
        accountId: accountId,
        deleted: false,
        email: newValue.email || null,
        firstName: newValue.firstName,
        isActive: newValue.isActive ?? true,
        locationIds: oldUserAccountData.locationIds || [],
        locations: oldUserAccountData.locations || [],
        phone: newValue.phone,
        pin: newValue.pin,
        accessCardKey: newValue.accessKey || null,
        roles: oldUserAccountData.roles || [],
        surname: newValue.lastName,
        accessId: newValue.roleId,
        gender: newValue.gender,
        ssn: newValue.ssn,
        dateOfBirth: newValue.dateOfBirth,
        addresses: oldUserAccountData.addresses || [],
        emergencyContactName: newValue.emergencyName,
        emergencyContactMeans: newValue.emergencyPhoneEmail,
        notes: oldUserAccountData.notes || [],
        userId: oldUserAccountData.userId || idgen.next(),
        inactiveReason: newValue.inactiveReason,
    }
}

export const composeUserAccountRole = (
    id: Maybe<string>,
    roleId: string,
    salaryRate: number,
    rateType: string,
    idgen: IdGenerator,
    userAccountId: string
): UserAccountRole => {
    return {
        id: id || idgen.next(),
        roleId: roleId,
        userAccountId: userAccountId,
        salaryRate: salaryRate,
        rateType: rateType,
    }
}

export const composeUserAccountAddress = (formValues: EmployeeFormValues, idgen: IdGenerator): UserAccountAddress => {
    return {
        id: formValues.addressId || idgen.next(),
        deleted: false,
        country: formValues.country,
        line1: formValues.line1,
        line2: formValues.line2,
        state: formValues.state,
        city: formValues.city,
        zip: formValues.zip,
    }
}

export const composeUserAccountNote = (
    userAccountId: string,
    note: string,
    selectedAccount: UserAccount,
    idgen: IdGenerator
): Partial<UserAccountNote> => {
    return {
        id: idgen.next(),
        note: note,
        userAccountId: userAccountId,
        deleted: false,
        originUserAccountId: selectedAccount.id,
        originTimestamp: getUtcTimestamp(),
        originUserName: selectedAccount && composeFullName(selectedAccount.firstName, selectedAccount.surname),
    }
}

export const getArrayTotalById = (id: string, data: Maybe<any[]>, totalKey: Maybe<string> = "total"): number => {
    const element = data?.find(el => el.id === id)
    return element ? (element[totalKey as string] ?? 0) : 0
}

export const getArrayTotalByName = (name: string, data: Maybe<any[]>): number => {
    return data?.find(el => el.name === name)?.total ?? 0
}

export const constructEmployeeCreatedPayload = (
    editorUserAccountId: string,
    formValues: EmployeeFormValues & {
        firstName: string
        roleId: string
    }
): EmployeeCreateRequestedEventData => {
    return {
        userId: uuid.v4(),
        id: uuid.v4(),
        editorUserAccountId: editorUserAccountId,
        ...(formValues.email ? { emailAddress: formValues.email } : {}),
        firstName: formValues.firstName,
        pin: formValues.pin,
        surname: formValues.lastName,
        emergencyContactName: formValues.emergencyName,
        emergencyContactMeans: formValues.emergencyPhoneEmail,
        phoneNumber: formValues.phone,
        ssn: formValues.ssn,
        dateOfBirth: formValues.dateOfBirth,
        accessCardKey: formValues.accessKey,
        gender: formValues.gender,
        inactiveReason: formValues.inactiveReason,
        accessLevelId: formValues.roleId,
        locations: constructEmployeeLocationEventData([], formValues.selectedLocationsIds ?? []),
        addresses: constructEmployeeAddressEventData([], formValues),
        positions: constructEmployeePositionEventData([], formValues.selectedPositions || []),
    }
}

export const constructEmployeeUpdatedPayload = (
    id: string,
    editorUserAccountId: Maybe<string>,
    formValues: EmployeeFormValues,
    userAccountOrigin: UserAccount
): EmployeeUpdateRequestedEventData => {
    return {
        id: id,
        editorUserAccountId: editorUserAccountId,
        ...(formValues.email ? { emailAddress: formValues.email } : {}),
        firstName: formValues.firstName,
        pin: formValues.pin,
        surname: formValues.lastName,
        emergencyContactName: formValues.emergencyName,
        emergencyContactMeans: formValues.emergencyPhoneEmail,
        phoneNumber: formValues.phone,
        ssn: formValues.ssn,
        dateOfBirth: formValues.dateOfBirth,
        accessCardKey: formValues.accessKey,
        isActive: formValues.isActive ?? undefined,
        gender: formValues.gender,
        inactiveReason: formValues.inactiveReason,
        accessLevelId: formValues.roleId,
        locations: constructEmployeeLocationEventData(
            userAccountOrigin?.locations || [],
            formValues.selectedLocationsIds ?? []
        ),
        addresses: constructEmployeeAddressEventData(userAccountOrigin?.addresses || [], formValues),
        positions: constructEmployeePositionEventData(
            userAccountOrigin?.roles || [],
            formValues.selectedPositions || []
        ),
        notes: constructEmployeeNotesEventData(formValues),
    }
}

export const constructEmployeeLocationEventData = (
    originUserAccountLocations: UserAccountLocation[],
    selectedLocationsIds: string[]
): EmployeeLocationEventData[] => {
    return selectedLocationsIds.map(locationId => {
        const userAccountLocation = originUserAccountLocations.find(ul => ul.locationId === locationId)
        return {
            id: userAccountLocation?.id || uuid.v4(),
            locationId: locationId,
        }
    })
}

export const constructEmployeePositionEventData = (
    originUserAccountPositions: UserAccountRole[],
    selectedPositions: SelectedPosition[]
): EmployeePositionEventData[] => {
    return selectedPositions.map(position => {
        const userAccountPosition = originUserAccountPositions.find(uap => uap.id === position.id)
        return {
            id: userAccountPosition?.id ?? uuid.v4(),
            roleId: position.positionId,
            salaryRate: position.rate,
            rateType: position.rateType,
        }
    })
}

export const constructEmployeeAddressEventData = (
    originUserAccountAddresses: UserAccountAddress[],
    formValues: EmployeeFormValues
): EmployeeAddressEventData[] => {
    const userAccountAddress = originUserAccountAddresses.find(ua => ua.id === formValues.addressId)

    const { line1, line2, state, city, country, zip } = formValues
    if ([line1, line2, state, city, country, zip].every(value => !value)) {
        return []
    }
    return [
        {
            id: userAccountAddress?.id || uuid.v4(),
            line1: formValues.line1,
            line2: formValues.line2,
            state: formValues.state,
            city: formValues.city,
            country: formValues.country,
            zip: formValues.zip,
        },
    ]
}

export const constructEmployeeNotesEventData = (formValues: EmployeeFormValues): EmployeeNoteEventData[] => {
    return (formValues.notes ?? []).map(note => {
        return {
            id: note.id,
            note: note.note,
            userAccountId: note.originUserAccountId,
        }
    })
}

export const updateUserAccountFromEmployeeForm = (
    payload: EmployeeFormValues,
    originUserAccount: UserAccount
): UserAccount => ({
    ...originUserAccount,
    email: payload.email || null,
    firstName: payload.firstName,
    isActive: payload.isActive ?? true,
    phone: payload.phone,
    pin: payload.pin,
    accessCardKey: payload.accessKey || null,
    surname: payload.lastName,
    accessId: payload.roleId,
    gender: payload.gender,
    ssn: payload.ssn,
    dateOfBirth: payload.dateOfBirth,
    emergencyContactName: payload.emergencyName,
    emergencyContactMeans: payload.emergencyPhoneEmail,
    inactiveReason: payload.inactiveReason,
    locationIds: payload.selectedLocationsIds ?? [],
    addresses: constructUserAccountAddressesFromEmployeePayload(payload),
    roles: constructUserAccountRolesFromEmployeePayload(payload, originUserAccount.id),
    notes: constructUserAccountNotesFromEmployeePayload(payload, originUserAccount.id),
})

export const createUserAccountFromEmployeeForm = (
    id: string,
    userId: string,
    accountId: string,
    payload: EmployeeFormValues
): UserAccount => ({
    id: id,
    userId: userId,
    accountId: accountId,
    deleted: false,
    email: payload.email || null,
    firstName: payload.firstName,
    middleName: null,
    isActive: payload.isActive ?? true,
    phone: payload.phone,
    pin: payload.pin,
    accessCardKey: payload.accessKey || null,
    surname: payload.lastName,
    accessId: payload.roleId,
    gender: payload.gender,
    ssn: payload.ssn,
    dateOfBirth: payload.dateOfBirth,
    emergencyContactName: payload.emergencyName,
    emergencyContactMeans: payload.emergencyPhoneEmail,
    inactiveReason: payload.inactiveReason,
    locationIds: payload.selectedLocationsIds ?? [],
    locations: [], // deprecated
    notes: [],
    employeeNumber: null,
    addresses: constructUserAccountAddressesFromEmployeePayload(payload),
    roles: constructUserAccountRolesFromEmployeePayload(payload, id),
})

export const constructUserAccountAddressesFromEmployeePayload = (payload: EmployeeFormValues): UserAccountAddress[] =>
    payload.addressId
        ? [
              {
                  id: payload.addressId,
                  country: payload.country,
                  line1: payload.line1,
                  line2: payload.line2,
                  state: payload.state,
                  city: payload.city,
                  zip: payload.zip,
                  deleted: false,
              },
          ]
        : []

export const constructEmployeeNoteFromPayload = (
    note: string,
    firstName: Maybe<string>,
    sureName: Maybe<string>,
    originUserAccountId: Maybe<string>
): EmployeeNote => {
    return {
        id: uuid.v7(),
        note: note,
        originUserName: composeFullName(firstName, sureName),
        originUserAccountId: originUserAccountId,
        originTimestamp: getIsoDateTimeNow(),
    }
}

export const constructUserAccountNotesFromEmployeePayload = (
    payload: EmployeeFormValues,
    userAccountId: string
): UserAccountNote[] => {
    return (payload.notes ?? []).map(note => ({
        id: note.id,
        note: note.note,
        originUserName: note.originUserName,
        originUserAccountId: note.originUserAccountId,
        originTimestamp: note.originTimestamp,
        deleted: false,
        userAccountId: userAccountId,
    }))
}

export const constructUserAccountRolesFromEmployeePayload = (
    payload: EmployeeFormValues,
    userAccountId: string
): UserAccountRole[] => {
    return (payload.selectedPositions ?? []).map(position => ({
        id: position.id,
        roleId: position.positionId,
        salaryRate: position.rate,
        rateType: position.rateType,
        userAccountId: userAccountId,
    }))
}

export const getEnrichedFormValuesFromEventData = (
    originFormValues: EmployeeFormValues,
    addresses: EmployeeAddressEventData[] = []
): EmployeeFormValues => {
    const [firstAddress] = addresses
    return {
        ...originFormValues,
        addressId: firstAddress?.id || originFormValues.addressId,
    }
}

export const constructUserAccountIdForEmployeeEvents = (
    signedUserAccountId: Maybe<string>,
    isSuperUser: boolean,
    organizationId: Maybe<string>,
    userAccounts: UserAccount[]
): Maybe<string> => {
    if (signedUserAccountId) {
        return signedUserAccountId
    }

    // TODO remove this after data/user-accounts is removed from Java
    // We have to use just first user account id for superusers, as it was before.
    // Logic under will not work for now - java is referencing to user_account_table when fetching user accounts and it would fail on 'artificial' user account for superusers
    if (userAccounts && userAccounts.length > 0) {
        return userAccounts[0].id
    }

    if (isSuperUser) {
        return getSuperuserUserAccountId()
    }

    if (organizationId) {
        return getOrganizationUserAccountId(organizationId)
    }

    return null
}

// Check README -> Superuser section for more info
export const getSuperuserUserAccountId = (): string => {
    return uuid.v5("superuser", "cda3e028-eb4c-4f0e-91f6-e0152aa05ee9")
}

// Check README -> Organization User Account ID
export const getOrganizationUserAccountId = (organizationId: string) => {
    return uuid.v5(organizationId, "4fac50b8-5ade-4bd0-a3a6-45a14cf3d8b2")
}
